/* =========================================================
   PATRIA SHOP — interacciones y animaciones
   Sin dependencias. Todo respeta "reducir movimiento" y se
   vuelve a inicializar cuando se edita una sección en el
   personalizador de Shopify.
   ========================================================= */
(function () {
  'use strict';

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var desktop = window.matchMedia('(min-width: 990px)');

  var parallaxItems = [];
  var driftItems = [];
  var progressItems = [];
  var sliders = [];
  var ticking = false;
  var observers = [];

  /* ---------- Scroll (parallax y deriva del texto gigante) ---------- */
  function onScroll() {
    if (ticking) return;
    ticking = true;
    window.requestAnimationFrame(function () {
      var vh = window.innerHeight;

      parallaxItems.forEach(function (item) {
        var rect = item.host.getBoundingClientRect();
        if (rect.bottom < 0 || rect.top > vh) return;
        var progress = (vh - rect.top) / (vh + rect.height); // 0 → 1 mientras cruza la pantalla
        item.el.style.transform = 'translate3d(0,' + ((progress - 0.5) * -item.speed * 100).toFixed(1) + 'px,0)';
      });

      progressItems.forEach(function (item) {
        var rect = item.host.getBoundingClientRect();
        var p = Math.min(1, Math.max(0, (vh * 0.35 - rect.top) / Math.max(rect.height, 1)));
        item.bar.style.transform = 'scaleX(' + p.toFixed(3) + ')';
      });

      driftItems.forEach(function (item) {
        var rect = item.host.getBoundingClientRect();
        if (rect.bottom < 0 || rect.top > vh) return;
        var progress = (vh - rect.top) / (vh + rect.height);
        item.el.style.transform = 'translate3d(' + (-progress * item.distance).toFixed(1) + 'px,0,0)';
      });

      ticking = false;
    });
  }

  /* ---------- Reveal al hacer scroll ---------- */
  function initReveal(root) {
    var targets = Array.prototype.slice.call(root.querySelectorAll('[data-reveal], [data-observe]'));
    if (!targets.length) return;

    // Escalonado dentro de un mismo grupo
    root.querySelectorAll('[data-reveal-group]').forEach(function (group) {
      group.querySelectorAll('[data-reveal]').forEach(function (el, i) {
        el.style.setProperty('--reveal-i', Math.min(i, 8));
      });
    });

    if (reduceMotion || !('IntersectionObserver' in window)) {
      targets.forEach(function (el) {
        el.classList.add('is-in');
      });
      return;
    }

    var io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-in');
          io.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.12 }
    );
    targets.forEach(function (el) {
      io.observe(el);
    });
    observers.push(io);

    // Al llegar al final de la página se muestra lo que quede a la vista (p. ej. la barra inferior
    // del pie), porque el margen del observador puede dejarlo siempre oculto en pantallas altas.
    function revealAtBottom() {
      var atBottom = window.innerHeight + window.pageYOffset >= document.documentElement.scrollHeight - 8;
      if (!atBottom) return;
      targets.forEach(function (el) {
        if (el.classList.contains('is-in')) return;
        if (el.getBoundingClientRect().top < window.innerHeight) {
          el.classList.add('is-in');
          io.unobserve(el);
        }
      });
    }
    window.addEventListener('scroll', revealAtBottom, { passive: true });
    window.addEventListener('resize', revealAtBottom);
    observers.push({
      disconnect: function () {
        window.removeEventListener('scroll', revealAtBottom);
        window.removeEventListener('resize', revealAtBottom);
      }
    });
    revealAtBottom();
  }

  /* ---------- Hero ---------- */
  function initHero(root) {
    root.querySelectorAll('.p-hero').forEach(function (hero) {
      // Dispara la secuencia de entrada tras el primer pintado
      window.requestAnimationFrame(function () {
        window.requestAnimationFrame(function () {
          hero.classList.add('is-ready');
        });
      });

      if (reduceMotion) return;

      var media = hero.querySelector('[data-parallax]');
      if (media) {
        parallaxItems.push({ host: hero, el: media, speed: parseFloat(media.getAttribute('data-parallax')) || 0.6 });
      }

      var word = hero.querySelector('[data-drift]');
      if (word) {
        driftItems.push({ host: hero, el: word, distance: parseFloat(word.getAttribute('data-drift')) || 260 });
      }
    });
  }

  /* ---------- Paneles de categorías (acordeón horizontal) ---------- */
  function initPanels(root) {
    root.querySelectorAll('[data-panels]').forEach(function (row) {
      var panels = Array.prototype.slice.call(row.querySelectorAll('.p-panel'));
      if (!panels.length || row.getAttribute('data-p-init')) return;
      row.setAttribute('data-p-init', '1');

      function activate(panel) {
        if (!desktop.matches) return;
        panels.forEach(function (p) {
          var on = p === panel;
          p.classList.toggle('is-active', on);
        });
      }

      panels.forEach(function (panel) {
        panel.addEventListener('mouseenter', function () {
          activate(panel);
        });
        panel.addEventListener('focusin', function () {
          activate(panel);
        });
      });

      // Estado inicial: el primero abierto en escritorio
      panels[0].classList.add('is-active');
      desktop.addEventListener('change', function () {
        if (desktop.matches) activate(row.querySelector('.p-panel.is-active') || panels[0]);
      });
    });
  }

  /* ---------- Historias con scroll (sticky) ---------- */
  function initStory(root) {
    root.querySelectorAll('[data-story]').forEach(function (story) {
      var steps = Array.prototype.slice.call(story.querySelectorAll('.p-story__step'));
      var images = Array.prototype.slice.call(story.querySelectorAll('.p-story__frame'));
      if (!steps.length) return;

      function setActive(index) {
        steps.forEach(function (s, i) {
          s.classList.toggle('is-active', i === index);
        });
        images.forEach(function (img, i) {
          img.classList.toggle('is-active', i === index);
        });
      }
      setActive(0);

      if (!('IntersectionObserver' in window)) return;

      // La franja central de la pantalla decide qué paso está activo
      var io = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (entry.isIntersecting) setActive(steps.indexOf(entry.target));
          });
        },
        { rootMargin: '-45% 0px -45% 0px', threshold: 0 }
      );
      steps.forEach(function (s) {
        io.observe(s);
      });
      observers.push(io);
    });
  }

  /* ---------- Journal: la imagen cambia según el artículo ---------- */
  function initJournal(root) {
    root.querySelectorAll('[data-journal]').forEach(function (journal) {
      var items = Array.prototype.slice.call(journal.querySelectorAll('.p-journal__item'));
      var slides = Array.prototype.slice.call(journal.querySelectorAll('.p-journal__slide'));
      if (!items.length || items.length !== slides.length || journal.getAttribute('data-p-init')) return;
      journal.setAttribute('data-p-init', '1');

      function setActive(index) {
        items.forEach(function (it, i) {
          it.classList.toggle('is-active', i === index);
        });
        slides.forEach(function (sl, i) {
          sl.classList.toggle('is-active', i === index);
        });
      }
      setActive(0);

      items.forEach(function (item, i) {
        item.addEventListener('mouseenter', function () {
          setActive(i);
        });
        item.addEventListener('focus', function () {
          setActive(i);
        });
      });
    });
  }

  /* ---------- Pie: volver arriba ---------- */
  function initFooter(root) {
    root.querySelectorAll('[data-back-to-top]').forEach(function (btn) {
      if (btn.getAttribute('data-p-init')) return;
      btn.setAttribute('data-p-init', '1');
      btn.addEventListener('click', function () {
        window.scrollTo({ top: 0, behavior: reduceMotion ? 'auto' : 'smooth' });
        var top = document.querySelector('.header__heading-link') || document.querySelector('.skip-to-content-link');
        if (top) top.focus({ preventScroll: true });
      });
    });
  }

  /* ---------- Menú móvil: Shop y More son acordeones propios ----------
     El script del menú de Dawn escucha el clic de todos los <summary> y marca la lista
     principal como "submenu-open", lo que la oculta (menú en blanco). Aquí se corta ese
     evento antes de que llegue al summary; el <details> sigue abriéndose por su cuenta. */
  function initDrawerSub(root) {
    root.querySelectorAll('details.p-drawer-sub').forEach(function (d) {
      if (d.getAttribute('data-p-sub')) return;
      d.setAttribute('data-p-sub', '1');
      d.addEventListener('click', function (e) { e.stopPropagation(); }, true);
    });
  }

  /* ---------- Pie de página: columnas desplegables en móvil ---------- */
  function initFooterAccordion(root) {
    var mq = window.matchMedia ? window.matchMedia('(max-width: 749px)') : null;
    root.querySelectorAll('[data-acc]').forEach(function (col) {
      if (col.getAttribute('data-acc-init')) return;
      var h = col.querySelector('.p-footer__heading');
      if (!h) return;
      col.setAttribute('data-acc-init', '1');
      var label = h.textContent.trim();
      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'p-footer__acc';
      btn.textContent = label;
      h.textContent = '';
      h.appendChild(btn);
      var uid = 'pfa-' + Math.random().toString(36).slice(2, 8);
      var panel = document.createElement('div');
      panel.className = 'p-footer__panel';
      panel.id = uid;
      while (h.nextSibling) panel.appendChild(h.nextSibling);
      col.appendChild(panel);
      btn.setAttribute('aria-controls', uid);
      function sync() {
        var mobile = mq && mq.matches;
        if (mobile) {
          btn.setAttribute('aria-expanded', col.classList.contains('is-open') ? 'true' : 'false');
          btn.tabIndex = 0;
        } else {
          btn.removeAttribute('aria-expanded');
          btn.tabIndex = -1;
          col.classList.remove('is-open');
        }
      }
      btn.addEventListener('click', function () {
        if (!(mq && mq.matches)) return;
        col.classList.toggle('is-open');
        sync();
      });
      if (mq) {
        if (mq.addEventListener) mq.addEventListener('change', sync);
        else if (mq.addListener) mq.addListener(sync);
      }
      sync();
    });
  }

  /* ---------- Artículo: progreso de lectura y copiar enlace ---------- */
  function initArticle(root) {
    root.querySelectorAll('[data-article]').forEach(function (art) {
      var body = art.querySelector('[data-art-body]');
      var bar = document.querySelector('[data-progress]');
      if (body && bar) progressItems.push({ host: body, bar: bar });
    });

    root.querySelectorAll('[data-copy-link]').forEach(function (btn) {
      if (btn.getAttribute('data-p-init')) return;
      btn.setAttribute('data-p-init', '1');
      btn.addEventListener('click', function () {
        var url = btn.getAttribute('data-copy-link');
        var label = btn.textContent;
        var done = btn.getAttribute('data-copied') || 'Copied!';

        function ok() {
          btn.textContent = done;
          btn.classList.add('is-copied');
          window.setTimeout(function () {
            btn.textContent = label;
            btn.classList.remove('is-copied');
          }, 2000);
        }

        function fallback() {
          var t = document.createElement('textarea');
          t.value = url;
          t.setAttribute('readonly', '');
          t.style.position = 'fixed';
          t.style.opacity = '0';
          document.body.appendChild(t);
          t.select();
          try {
            if (document.execCommand('copy')) ok();
          } catch (e) {}
          document.body.removeChild(t);
        }

        if (navigator.clipboard && navigator.clipboard.writeText) {
          navigator.clipboard.writeText(url).then(ok, fallback);
        } else {
          fallback();
        }
      });
    });
  }

  /* ---------- Hero: slideshow de imágenes ---------- */
  function initHeroSlider(root) {
    root.querySelectorAll('[data-hero-slider]').forEach(function (hero) {
      if (hero.getAttribute('data-p-init')) return;
      var slides = Array.prototype.slice.call(hero.querySelectorAll('[data-slide]'));
      if (slides.length < 2) return;
      hero.setAttribute('data-p-init', '1');

      var panels = Array.prototype.slice.call(hero.querySelectorAll('[data-panel-id]'));
      var dots = Array.prototype.slice.call(hero.querySelectorAll('[data-dot]'));
      var toggle = hero.querySelector('[data-hero-toggle]');
      var interval = parseInt(hero.getAttribute('data-interval'), 10) || 6000;
      var wantsAuto = hero.getAttribute('data-autoplay') === 'true' && !reduceMotion;

      // Todos los eventos se registran aquí para poder quitarlos al reiniciar la sección
      var bound = [];
      function on(target, type, fn, opts) {
        target.addEventListener(type, fn, opts);
        bound.push([target, type, fn, opts]);
      }

      var index = 0;
      var timer = null;
      var userPaused = false;
      var hovering = false;
      var tabHidden = document.hidden;
      var offscreen = false;
      var io = null;

      function canRun() {
        return wantsAuto && !userPaused && !hovering && !tabHidden && !offscreen;
      }

      function clearTimer() {
        if (timer) {
          window.clearTimeout(timer);
          timer = null;
        }
      }

      // Reinicia el temporizador y la barra de progreso desde cero
      function schedule() {
        clearTimer();
        hero.classList.remove('is-playing');
        hero.classList.toggle('is-hovered', hovering);
        hero.classList.toggle('is-paused', userPaused);
        if (!canRun()) return;
        void hero.offsetWidth; // fuerza el reinicio de la animación CSS
        hero.classList.add('is-playing');
        timer = window.setTimeout(function () {
          show(index + 1);
        }, interval);
      }

      function show(i) {
        index = (i + slides.length) % slides.length;
        slides.forEach(function (s, k) {
          s.classList.toggle('is-active', k === index);
        });
        dots.forEach(function (d, k) {
          d.classList.toggle('is-active', k === index);
          d.setAttribute('aria-current', k === index ? 'true' : 'false');
        });
        // Los colores de letras, degradado y controles siguen al slide activo
        hero.setAttribute('data-tone', slides[index].getAttribute('data-tone') || 'light');
        // El texto solo se anima si el slide trae un panel distinto al actual
        var panelId = slides[index].getAttribute('data-panel');
        panels.forEach(function (p) {
          p.classList.toggle('is-active', p.getAttribute('data-panel-id') === panelId);
        });
        schedule();
      }

      dots.forEach(function (d, k) {
        on(d, 'click', function () {
          show(k);
        });
      });

      var prev = hero.querySelector('[data-hero-prev]');
      var next = hero.querySelector('[data-hero-next]');
      if (prev) on(prev, 'click', function () { show(index - 1); });
      if (next) on(next, 'click', function () { show(index + 1); });

      if (toggle) {
        on(toggle, 'click', function () {
          userPaused = !userPaused;
          toggle.setAttribute(
            'aria-label',
            userPaused ? toggle.getAttribute('data-label-play') : toggle.getAttribute('data-label-pause')
          );
          schedule();
        });
      }

      // Pausa mientras el cursor o el teclado están dentro del hero
      on(hero, 'mouseenter', function () { hovering = true; schedule(); });
      on(hero, 'mouseleave', function () { hovering = false; schedule(); });
      on(hero, 'focusin', function () { hovering = true; schedule(); });
      on(hero, 'focusout', function () { hovering = false; schedule(); });

      // Deslizar con el dedo
      var touchX = null;
      on(hero, 'touchstart', function (e) { touchX = e.touches[0].clientX; }, { passive: true });
      on(hero, 'touchend', function (e) {
        if (touchX === null) return;
        var dx = e.changedTouches[0].clientX - touchX;
        touchX = null;
        if (Math.abs(dx) > 50) show(dx < 0 ? index + 1 : index - 1);
      }, { passive: true });

      // Flechas del teclado cuando el foco está en los controles
      on(hero, 'keydown', function (e) {
        if (!e.target.closest('.p-hero__controls')) return;
        if (e.key === 'ArrowRight') show(index + 1);
        if (e.key === 'ArrowLeft') show(index - 1);
      });

      // No gastar recursos si la pestaña o el hero no se ven
      function onVisibility() { tabHidden = document.hidden; schedule(); }
      on(document, 'visibilitychange', onVisibility);
      if ('IntersectionObserver' in window) {
        io = new IntersectionObserver(function (entries) {
          offscreen = !entries[0].isIntersecting;
          schedule();
        }, { threshold: 0.2 });
        io.observe(hero);
      }

      // Personalizador de Shopify: al seleccionar un slide se muestra y se detiene la rotación
      on(hero, 'shopify:block:select', function (e) {
        var target = e.target.closest ? e.target.closest('[data-slide]') : null;
        var k = slides.indexOf(target);
        userPaused = true;
        if (k > -1) show(k); else schedule();
      });
      on(hero, 'shopify:block:deselect', function () {
        userPaused = false;
        schedule();
      });

      sliders.push({
        destroy: function () {
          clearTimer();
          bound.forEach(function (b) { b[0].removeEventListener(b[1], b[2], b[3]); });
          bound = [];
          if (io) io.disconnect();
          hero.classList.remove('is-playing');
          hero.removeAttribute('data-p-init');
        }
      });

      schedule();
    });
  }

  /* ---------- Ventanas (guía de tallas) ---------- */
  function initDialogs(root) {
    root.querySelectorAll('[data-dialog-open]').forEach(function (btn) {
      if (btn.getAttribute('data-p-init')) return;
      btn.setAttribute('data-p-init', '1');
      var dlg = document.getElementById(btn.getAttribute('data-dialog-open'));
      if (!dlg) return;

      function lock(on) {
        document.documentElement.classList.toggle('p-lock', on);
      }

      btn.addEventListener('click', function () {
        if (typeof dlg.showModal === 'function') dlg.showModal();
        else dlg.setAttribute('open', '');
        lock(true);
      });

      dlg.addEventListener('close', function () {
        lock(false);
        btn.focus();
      });

      // Cerrar con la X o haciendo clic fuera del panel
      dlg.addEventListener('click', function (e) {
        if (e.target === dlg || e.target.closest('[data-dialog-close]')) {
          if (typeof dlg.close === 'function') dlg.close();
          else {
            dlg.removeAttribute('open');
            lock(false);
          }
        }
      });
    });
  }

  /* ---------- Ventana emergente de bienvenida ---------- */
  function initPopup(root) {
    root.querySelectorAll('[data-popup]').forEach(function (dlg) {
      if (dlg.getAttribute('data-p-init')) return;
      dlg.setAttribute('data-p-init', '1');

      var KEY_CLOSED = 'patria_popup_closed';
      var KEY_DONE = 'patria_popup_done';
      var days = parseFloat(dlg.getAttribute('data-days')) || 7;
      var trigger = dlg.getAttribute('data-trigger') || 'delay';
      var delay = (parseFloat(dlg.getAttribute('data-delay')) || 8) * 1000;
      var scrollPct = parseFloat(dlg.getAttribute('data-scroll')) || 40;
      var inEditor = !!(window.Shopify && window.Shopify.designMode);
      var timer = null;
      var opened = false;

      // El almacenamiento puede estar bloqueado (modo privado): nunca debe romper la página
      function read(k) {
        try { return window.localStorage.getItem(k); } catch (e) { return null; }
      }
      function write(k, v) {
        try { window.localStorage.setItem(k, v); } catch (e) {}
      }

      function otherOverlayOpen() {
        return !!(document.querySelector('dialog[open]:not([data-popup])') || document.querySelector('cart-drawer.active'));
      }

      function open() {
        if (dlg.open) return;
        opened = true;
        if (typeof dlg.showModal === 'function') dlg.showModal();
        else dlg.setAttribute('open', '');
        document.documentElement.classList.add('p-lock');
        var field = dlg.querySelector('input[type="email"]');
        if (field) field.focus({ preventScroll: true });
      }

      function close() {
        if (typeof dlg.close === 'function') dlg.close();
        else dlg.removeAttribute('open');
      }

      dlg.addEventListener('close', function () {
        document.documentElement.classList.remove('p-lock');
        if (!inEditor && !dlg.querySelector('[data-popup-success]')) write(KEY_CLOSED, String(Date.now()));
      });

      // Cerrar con la X, con "seguir navegando" o haciendo clic en el fondo oscuro
      dlg.addEventListener('click', function (e) {
        if (e.target === dlg || e.target.closest('[data-popup-close]')) close();
      });

      // Después de suscribirse (o si hubo un error) la página se recarga: se abre al instante con el resultado
      if (dlg.querySelector('[data-popup-success]')) {
        if (!inEditor) write(KEY_DONE, '1');
        open();
        return;
      }
      if (dlg.querySelector('[data-popup-error]')) {
        open();
        return;
      }

      // En el editor de Shopify nunca se abre sola: solo al seleccionar la sección
      if (inEditor) {
        document.addEventListener('shopify:section:select', function (e) {
          if (e.target && e.target.contains(dlg)) open();
        });
        document.addEventListener('shopify:section:deselect', function (e) {
          if (e.target && e.target.contains(dlg)) close();
        });
        return;
      }

      if (read(KEY_DONE)) return;
      var last = parseFloat(read(KEY_CLOSED));
      if (last && Date.now() - last < days * 864e5) return;

      function tryOpen() {
        if (opened) return;
        if (otherOverlayOpen()) {
          // Si hay otra ventana abierta (carrito, guía de tallas…), reintenta en unos segundos
          timer = window.setTimeout(tryOpen, 4000);
          return;
        }
        open();
      }

      if (trigger === 'scroll') {
        var onScrollPopup = function () {
          var max = document.documentElement.scrollHeight - window.innerHeight;
          if (max > 0 && window.scrollY / max * 100 >= scrollPct) {
            window.removeEventListener('scroll', onScrollPopup);
            tryOpen();
          }
        };
        window.addEventListener('scroll', onScrollPopup, { passive: true });
      } else {
        timer = window.setTimeout(tryOpen, delay);
      }
    });
  }

  /* ---------- Quiz de nostalgia ---------- */
  function initQuiz(root) {
    root.querySelectorAll('[data-quiz]').forEach(function (quiz) {
      if (quiz.getAttribute('data-p-init')) return;
      quiz.setAttribute('data-p-init', '1');
      var total = parseInt(quiz.getAttribute('data-total'), 10) || 0;
      var screens = {};
      quiz.querySelectorAll('[data-quiz-screen]').forEach(function (s) { screens[s.getAttribute('data-quiz-screen')] = s; });
      var qs = quiz.querySelectorAll('[data-quiz-q]');
      var bar = quiz.querySelector('[data-quiz-bar]');
      var stepEl = quiz.querySelector('[data-quiz-step]');
      var tagsInput = quiz.querySelector('[data-quiz-tags]');
      var baseTags = tagsInput ? tagsInput.value : '';
      var answers = [];
      var current = 0;
      var winner = '';

      function show(name) {
        Object.keys(screens).forEach(function (k) { screens[k].classList.toggle('is-active', k === name); });
        var top = quiz.getBoundingClientRect().top;
        if (top < 0) quiz.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
      function showQ(i) {
        current = i;
        qs.forEach(function (q, idx) {
          q.hidden = idx !== i;
          if (idx === i) {
            q.querySelectorAll('.p-quiz__opt').forEach(function (b) { b.classList.remove('is-selected'); });
          }
        });
        if (stepEl) stepEl.textContent = i + 1;
        if (bar) bar.style.width = (((i + 1) / total) * 100) + '%';
        show('questions');
      }
      function compute() {
        var score = {}, order = [];
        answers.forEach(function (p) {
          if (!(p in score)) { score[p] = 0; order.push(p); }
          score[p] += 1;
        });
        var best = order[0], max = 0;
        order.forEach(function (p) { if (score[p] > max) { max = score[p]; best = p; } });
        return best;
      }
      function showResult() {
        var found = false;
        quiz.querySelectorAll('[data-quiz-res]').forEach(function (r) {
          var on = r.getAttribute('data-quiz-res') === winner;
          r.hidden = !on;
          if (on) found = true;
        });
        if (!found) {
          var first = quiz.querySelector('[data-quiz-res]');
          if (first) first.hidden = false;
        }
        show('result');
      }

      quiz.querySelector('[data-quiz-start]').addEventListener('click', function () {
        answers = [];
        showQ(0);
      });
      quiz.querySelector('[data-quiz-back]').addEventListener('click', function () {
        if (current === 0) { show('intro'); return; }
        answers.pop();
        showQ(current - 1);
      });
      quiz.querySelectorAll('.p-quiz__opt').forEach(function (btn) {
        btn.addEventListener('click', function () {
          btn.classList.add('is-selected');
          answers[current] = btn.getAttribute('data-profile');
          window.setTimeout(function () {
            if (current + 1 < total) {
              showQ(current + 1);
            } else {
              winner = compute();
              if (tagsInput) tagsInput.value = baseTags + ',miss-' + winner;
              show('gate');
            }
          }, 320);
        });
      });

      var form = quiz.querySelector('form');
      if (form) {
        form.addEventListener('submit', function (e) {
          e.preventDefault();
          if (!form.reportValidity()) return;
          var btn = form.querySelector('[type="submit"]');
          if (btn) btn.disabled = true;
          var done = function () { if (btn) btn.disabled = false; showResult(); };
          try {
            fetch(form.action, { method: 'POST', body: new FormData(form), headers: { Accept: 'text/html' } }).then(done, done);
          } catch (err) { done(); }
        });
      }
      quiz.querySelectorAll('[data-quiz-retake]').forEach(function (b) {
        b.addEventListener('click', function () { answers = []; show('intro'); });
      });
    });
  }

  /* ---------- Instagram: videos que se reproducen al verse ---------- */
  function initInstaVideo(root) {
    var vids = root.querySelectorAll('[data-insta-video]');
    if (!vids.length) return;
    var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce || !('IntersectionObserver' in window)) return;
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (en) {
        var v = en.target;
        if (en.isIntersecting) {
          var p = v.play();
          if (p && p.catch) p.catch(function () {});
        } else {
          v.pause();
        }
      });
    }, { threshold: 0.5 });
    vids.forEach(function (v) {
      if (v.getAttribute('data-p-init')) return;
      v.setAttribute('data-p-init', '1');
      v.muted = true;
      io.observe(v);
    });
    observers.push(io);
  }

  /* ---------- Instagram: feed automático + carrusel rotativo ---------- */
  function initInsta(root) {
    root.querySelectorAll('[data-insta]').forEach(function (el) {
      if (el.__instaReady) return;
      el.__instaReady = true;
      var track = el.querySelector('[data-insta-track]');
      if (!track) return;
      var prev = el.querySelector('[data-insta-prev]');
      var next = el.querySelector('[data-insta-next]');
      var feed = (el.getAttribute('data-feed') || '').trim();
      var limit = parseInt(el.getAttribute('data-limit'), 10) || 12;
      var interval = Math.max(2000, parseInt(el.getAttribute('data-interval'), 10) || 4000);
      var autoplay = el.getAttribute('data-autoplay') === 'true';
      var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      var timer = null, paused = false, visible = true;

      function step() {
        var item = track.querySelector('.p-insta__item');
        if (!item) return 0;
        var gap = parseFloat(window.getComputedStyle(track).columnGap) || 0;
        return item.getBoundingClientRect().width + gap;
      }
      function go(dir) {
        var max = track.scrollWidth - track.clientWidth;
        if (max <= 4) return;
        var x = track.scrollLeft;
        var target = x + dir * step();
        if (dir > 0 && x >= max - 4) target = 0;
        else if (dir < 0 && x <= 4) target = max;
        track.scrollTo({ left: target, behavior: reduce ? 'auto' : 'smooth' });
      }
      function stop() { if (timer) { window.clearInterval(timer); timer = null; } }
      function start() {
        stop();
        if (!autoplay || reduce || paused || !visible || document.hidden) return;
        timer = window.setInterval(function () { go(1); }, interval);
      }
      if (prev) prev.addEventListener('click', function () { go(-1); start(); });
      if (next) next.addEventListener('click', function () { go(1); start(); });
      el.addEventListener('mouseenter', function () { paused = true; stop(); });
      el.addEventListener('mouseleave', function () { paused = false; start(); });
      el.addEventListener('focusin', function () { paused = true; stop(); });
      el.addEventListener('focusout', function () { paused = false; start(); });
      document.addEventListener('visibilitychange', start);
      if ('IntersectionObserver' in window) {
        var io = new IntersectionObserver(function (entries) {
          visible = entries[0].isIntersecting;
          start();
        });
        io.observe(el);
        observers.push(io);
      }
      observers.push({ disconnect: function () { stop(); document.removeEventListener('visibilitychange', start); } });

      function build(post) {
        var img = (post.sizes && post.sizes.medium && post.sizes.medium.mediaUrl) ||
          post.thumbnailUrl || post.mediaUrl;
        if (!img || !post.permalink) return null;
        var li = document.createElement('li');
        li.className = 'p-insta__item';
        var a = document.createElement('a');
        a.className = 'p-insta__tile';
        a.href = post.permalink;
        a.target = '_blank';
        a.rel = 'noopener noreferrer';
        a.setAttribute('aria-label', 'View post on Instagram');
        var im = document.createElement('img');
        im.src = img;
        im.alt = post.altText || post.prunedCaption || '';
        im.loading = 'lazy';
        im.decoding = 'async';
        a.appendChild(im);
        if (post.mediaType === 'VIDEO' || post.isReel) {
          var b = document.createElement('span');
          b.className = 'p-insta__badge';
          b.setAttribute('aria-hidden', 'true');
          b.innerHTML = '<svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor" focusable="false"><path d="M8 5v14l11-7z"></path></svg>';
          a.appendChild(b);
        }
        var tpl = el.querySelector('template[data-insta-head]');
        if (tpl && tpl.content) li.appendChild(tpl.content.cloneNode(true));
        li.appendChild(a);
        return li;
      }

      if (/^https:\/\/feeds\.behold\.so\//.test(feed) && window.fetch) {
        fetch(feed, { credentials: 'omit' })
          .then(function (r) { return r.ok ? r.json() : Promise.reject(); })
          .then(function (data) {
            var posts = (data && data.posts) || [];
            var items = [];
            posts.slice(0, limit).forEach(function (p) { var li = build(p); if (li) items.push(li); });
            if (!items.length) return;
            while (track.firstChild) track.removeChild(track.firstChild);
            items.forEach(function (li) { track.appendChild(li); });
            var follow = el.querySelector('[data-insta-follow]');
            if (follow && data.username && (!follow.getAttribute('href') || follow.getAttribute('href') === '#')) {
              follow.href = 'https://www.instagram.com/' + encodeURIComponent(data.username);
            }
            track.scrollLeft = 0;
          })
          .catch(function () {});
      }
      start();
    });
  }

  /* ---------- Arranque ---------- */
  function init(root) {
    root = root || document;
    initReveal(root);
    initHero(root);
    initHeroSlider(root);
    initPanels(root);
    initStory(root);
    initJournal(root);
    initFooter(root);
    initFooterAccordion(root);
    initDrawerSub(root);
    initArticle(root);
    initDialogs(root);
    initPopup(root);
    initInsta(root);
    initInstaVideo(root);
    initQuiz(root);
    onScroll();
  }

  function teardown() {
    observers.forEach(function (io) {
      io.disconnect();
    });
    observers = [];
    parallaxItems = [];
    driftItems = [];
    progressItems = [];
    sliders.forEach(function (s) { s.destroy(); });
    sliders = [];
  }

  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', onScroll, { passive: true });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init(document);
    });
  } else {
    init(document);
  }

  // Personalizador de Shopify: volver a iniciar cuando cambia una sección
  document.addEventListener('shopify:section:load', function () {
    teardown();
    init(document);
  });

  // Deslizador de precio doble: mueve los campos DE / A del filtro y envía al soltar
  if (window.customElements && !customElements.get('patria-range')) {
    customElements.define(
      'patria-range',
      class PatriaRange extends HTMLElement {
        connectedCallback() {
          var host = this.closest('details') || this.parentElement;
          var fields = host ? host.querySelectorAll('price-range input') : [];
          this.textMin = fields[0];
          this.textMax = fields[1];
          this.rMin = this.querySelector('.p-range__input--min');
          this.rMax = this.querySelector('.p-range__input--max');
          if (!this.textMin || !this.textMax || !this.rMin || !this.rMax) return;
          var self = this;
          var max = parseInt(this.getAttribute('data-max'), 10) || 0;
          this.hasMin = this.textMin.value !== '';
          this.hasMax = this.textMax.value !== '';
          this.rMin.addEventListener('input', function () { self.sync('min'); });
          this.rMax.addEventListener('input', function () { self.sync('max'); });
          this.rMin.addEventListener('change', function () { self.commit(); });
          this.rMax.addEventListener('change', function () { self.commit(); });
          [this.textMin, this.textMax].forEach(function (t) {
            t.addEventListener('change', function () { self.fromText(); });
          });
          this.max = max;
          this.paint();
        }
        sync(which) {
          var lo = parseInt(this.rMin.value, 10);
          var hi = parseInt(this.rMax.value, 10);
          if (lo > hi) {
            if (which === 'min') { this.rMin.value = hi; lo = hi; } else { this.rMax.value = lo; hi = lo; }
          }
          this.hasMin = lo > 0;
          this.hasMax = hi < this.max;
          this.textMin.value = this.hasMin ? lo : '';
          this.textMax.value = this.hasMax ? hi : '';
          this.paint();
        }
        commit() {
          // Dispara el envío automático del formulario de filtros
          this.textMin.dispatchEvent(new Event('change', { bubbles: true }));
          this.textMax.dispatchEvent(new Event('input', { bubbles: true }));
        }
        fromText() {
          var lo = parseFloat(String(this.textMin.value).replace(',', '.'));
          var hi = parseFloat(String(this.textMax.value).replace(',', '.'));
          this.rMin.value = isNaN(lo) ? 0 : Math.max(0, Math.floor(lo));
          this.rMax.value = isNaN(hi) ? this.max : Math.min(this.max, Math.ceil(hi));
          this.paint();
        }
        paint() {
          var max = this.max || 1;
          this.style.setProperty('--lo', (parseInt(this.rMin.value, 10) / max) * 100 + '%');
          this.style.setProperty('--hi', (parseInt(this.rMax.value, 10) / max) * 100 + '%');
        }
      }
    );
  }
})();
